import React, { useCallback, useState } from "react";
import { Activity, ArrowLeft, Eye, EyeOff, Lock, Phone, Workflow, Bot, MessageSquareText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthShell } from "@packages/ui";
import { useAuthStore } from "../../stores/authStore";
import { LoginRequest, SmsCodePurpose } from "@packages/global";
import { appConfig } from "../../config/app.config";
import ThemeToggle from "../../components/ThemeToggle";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import AuthCaptcha from "./AuthCaptcha";
import SmsCodeCaptchaDialog from "./SmsCodeCaptchaDialog";

const LoginPage: React.FC = () => {
  const { t } = useTranslation("auth");
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [smsCaptchaOpen, setSmsCaptchaOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginType, setLoginType] = useState<LoginRequest["loginType"]>("password");
  const [formData, setFormData] = useState<LoginRequest>({
    account: "",
    password: "",
    code: "",
    captchaId: "",
    captchaCode: "",
    loginType: "password",
  });
  const [captchaRefreshKey, setCaptchaRefreshKey] = useState(0);
  const [errors, setErrors] = useState<{ account?: string; password?: string; code?: string; captcha?: string }>({});
  const { getCaptcha, login, sendSmsCode } = useAuthStore();
  const navigate = useNavigate();
  const features = [
    {
      icon: <Bot className="h-6 w-6" strokeWidth={2.2} />,
      title: t("login.featureModelTitle"),
      description: t("login.featureModelDescription"),
    },
    {
      icon: <Workflow className="h-6 w-6" strokeWidth={2.2} />,
      title: t("login.featureSkillTitle"),
      description: t("login.featureSkillDescription"),
    },
    {
      icon: <Activity className="h-6 w-6" strokeWidth={2.2} />,
      title: t("login.featureWorkspaceTitle"),
      description: t("login.featureWorkspaceDescription"),
    },
  ];

  const validateForm = (): boolean => {
    const nextErrors: { account?: string; password?: string; code?: string; captcha?: string } = {};
    if (!formData.account) {
      nextErrors.account = t("login.accountRequired");
    } else if (loginType === "sms" && !isValidPhone(formData.account)) {
      nextErrors.account = t("login.phoneInvalid");
    }
    if (loginType === "password" && !formData.password) {
      nextErrors.password = t("login.passwordRequired");
    }
    if (loginType === "password" && !formData.captchaCode) {
      nextErrors.captcha = t("login.captchaRequired");
    }
    if (loginType === "sms" && !formData.code) {
      nextErrors.code = t("login.codeRequired");
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await login({ ...formData, account: formData.account.trim(), loginType });
      navigate("/dashboard");
    } catch (error: any) {
      setErrors({ account: error.response?.data?.error || t("login.failed") });
      if (loginType === "password") {
        setCaptchaRefreshKey((value) => value + 1);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange =
    (field: "account" | "password" | "code") => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: event.target.value });
      if (errors[field]) {
        setErrors({ ...errors, [field]: undefined });
      }
    };

  const handleModeChange = (nextType: LoginRequest["loginType"]) => {
    setLoginType(nextType);
    setFormData((value) => ({ ...value, loginType: nextType }));
    setErrors({});
  };

  const handleCaptchaChange = useCallback((value: string) => {
    setFormData((current) => ({ ...current, captchaCode: value }));
    setErrors((current) => ({ ...current, captcha: undefined }));
  }, []);

  const handleCaptchaIdChange = useCallback((captchaId: string) => {
    setFormData((current) => ({ ...current, captchaId }));
  }, []);

  const handleSendCode = async () => {
    if (!isValidPhone(formData.account)) {
      setErrors({ account: t("login.phoneInvalid") });
      return;
    }
    setSmsCaptchaOpen(true);
  };

  const handleConfirmSendCode = async ({ captchaId, captchaCode }: { captchaId: string; captchaCode: string }) => {
    setSendingCode(true);
    try {
      await sendSmsCode({
        phone: formData.account.trim(),
        purpose: SmsCodePurpose.LOGIN,
        captchaId,
        captchaCode,
      });
      startCountdown(setCountdown);
    } finally {
      setSendingCode(false);
    }
  };

  return (
    <AuthShell
      appName={appConfig.name}
      appDescription={appConfig.description}
      title={t("login.title")}
      subtitle={t("login.shellSubtitle")}
      features={features}
      homeAction={
        <button
          type="button"
          onClick={() => { navigate("/"); }}
          className="inline-flex h-10 items-center gap-2 rounded-full border border-border-subtle bg-bg-surface/90 px-3 text-sm font-medium text-text-secondary shadow-sm backdrop-blur transition-colors hover:text-text-primary lg:border-white/60 lg:bg-white/10 lg:text-white lg:hover:bg-white/20 lg:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("login.backHome")}
        </button>
      }
      topBar={
        <>
          <div className="w-10">
            <ThemeToggle compact />
          </div>
          <div className="h-6 w-px bg-border-subtle" />
          <div className="w-10">
            <LanguageSwitcher compact />
          </div>
        </>
      }
      brandMark={
        <div className="grid grid-cols-2 gap-1.5">
          {[0, 1, 2, 3].map((cell) => (
            <div
              key={cell}
              className="h-[21px] w-[21px] rounded-[3px] border-[2.5px] border-white"
            />
          ))}
        </div>
      }
      brandActionLabel={t("login.backHome")}
      onBrandClick={() => { navigate("/"); }}
      supportMeta={t("login.supportMeta")}
      footerAction={<button type="button" className="font-medium">{t("login.support")}</button>}
      form={() => (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <label
              htmlFor="account"
              className="block text-sm font-medium text-text-primary"
            >
              {loginType === "sms" ? t("login.phone") : t("login.account")}
            </label>
            <div className="relative">
              <Phone className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
              <input
                id="account"
                name="account"
                type="text"
                autoComplete="username"
                value={formData.account}
                onChange={handleChange("account")}
                placeholder={loginType === "sms" ? t("login.phonePlaceholder") : t("login.accountPlaceholder")}
                className={`h-11 w-full rounded-lg border bg-bg-form pl-11 pr-4 text-sm text-text-primary placeholder:text-text-muted transition-colors focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/10 ${
                  errors.account ? "border-danger" : "border-border-default"
                }`}
              />
            </div>
            {errors.account ? <p className="text-sm text-danger">{errors.account}</p> : null}
          </div>

          {loginType === "password" ? (
            <>
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-text-primary">
                  {t("login.password")}
                </label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange("password")}
                    placeholder={t("login.passwordPlaceholder")}
                    className={`h-11 w-full rounded-lg border bg-bg-form pl-11 pr-11 text-sm text-text-primary placeholder:text-text-muted transition-colors focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/10 ${
                      errors.password ? "border-danger" : "border-border-default"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-text-muted transition-colors hover:text-text-primary"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password ? <p className="text-sm text-danger">{errors.password}</p> : null}
              </div>
              <AuthCaptcha
                value={formData.captchaCode || ""}
                error={errors.captcha}
                refreshSignal={captchaRefreshKey}
                getCaptcha={getCaptcha}
                onChange={handleCaptchaChange}
                onChallengeChange={handleCaptchaIdChange}
                label={t("login.captcha")}
                placeholder={t("login.captchaPlaceholder")}
                refreshLabel={t("login.refreshCaptcha")}
              />
            </>
          ) : (
            <>
              <div className="space-y-2">
                <label htmlFor="code" className="block text-sm font-medium text-text-primary">
                  {t("login.code")}
                </label>
                <div className="flex gap-2">
                  <div className="relative min-w-0 flex-1">
                    <MessageSquareText className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
                    <input
                      id="code"
                      name="code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={formData.code}
                      onChange={handleChange("code")}
                      placeholder={t("login.codePlaceholder")}
                      className={`h-11 w-full rounded-lg border bg-bg-form pl-11 pr-4 text-sm text-text-primary placeholder:text-text-muted transition-colors focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/10 ${
                        errors.code ? "border-danger" : "border-border-default"
                      }`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={sendingCode || countdown > 0}
                    className="h-11 w-[112px] shrink-0 rounded-lg border border-border-default bg-bg-form text-sm font-medium text-text-primary transition-colors hover:bg-bg-elevated disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {countdown > 0 ? `${countdown}s` : sendingCode ? t("login.sendingCode") : t("login.sendCode")}
                  </button>
                </div>
                {errors.code ? <p className="text-sm text-danger">{errors.code}</p> : null}
              </div>
            </>
          )}

          <SmsCodeCaptchaDialog
            isOpen={smsCaptchaOpen}
            onClose={() => setSmsCaptchaOpen(false)}
            getCaptcha={getCaptcha}
            onConfirm={handleConfirmSendCode}
            title={t("login.smsCaptchaTitle")}
            description={t("login.smsCaptchaDescription")}
            label={t("login.captcha")}
            placeholder={t("login.captchaPlaceholder")}
            refreshLabel={t("login.refreshCaptcha")}
            submitLabel={t("login.sendCode")}
            submittingLabel={t("login.sendingCode")}
            cancelLabel={t("login.cancel")}
            requiredMessage={t("login.captchaRequired")}
            failedMessage={t("login.codeSendFailed")}
          />

          <div className="flex items-center justify-between text-sm">
            <label className="inline-flex items-center gap-2 text-text-secondary">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border border-border-default bg-bg-form text-brand-600 focus:ring-brand-600"
              />
              <span>记住我</span>
            </label>
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="font-medium text-primary-ink transition-colors hover:text-primary-ink dark:text-brand-300 dark:hover:text-brand-200"
            >
              {t("login.forgotPassword")}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="h-11 w-full rounded-lg bg-action-primary text-sm font-semibold text-white shadow-[0_2px_8px_rgba(0,0,0,0.15)] transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading ? t("login.loggingIn") : t("login.submit")}
          </button>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border-subtle" />
            <span className="text-xs text-text-muted">或</span>
            <div className="h-px flex-1 bg-border-subtle" />
          </div>

          <button
            type="button"
            onClick={() => handleModeChange(loginType === "password" ? "sms" : "password")}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-border-default bg-bg-form text-sm font-medium text-text-primary transition-colors hover:bg-bg-elevated"
          >
            {loginType === "password" ? (
              <MessageSquareText className="h-4 w-4" />
            ) : (
              <Lock className="h-4 w-4" />
            )}
            {loginType === "password" ? t("login.smsMode") : t("login.passwordMode")}
          </button>

          <div className="flex justify-center gap-1 text-sm">
            <span className="text-text-secondary">还没有账号？</span>
            <button
              type="button"
              onClick={() => navigate("/register")}
              className="font-medium text-primary-ink transition-colors hover:text-primary-ink dark:text-brand-300 dark:hover:text-brand-200"
            >
              {t("login.registerLink")}
            </button>
          </div>
        </form>
      )}
    />
  );
};

function isValidPhone(phone: string): boolean {
  return /^1[3-9]\d{9}$/.test(phone.trim());
}

function startCountdown(setCountdown: React.Dispatch<React.SetStateAction<number>>) {
  setCountdown(60);
  const timer = window.setInterval(() => {
    setCountdown((value) => {
      if (value <= 1) {
        window.clearInterval(timer);
        return 0;
      }
      return value - 1;
    });
  }, 1000);
}

export default LoginPage;
